/**
 * VideoGrid — root participant layout (T009 / US1, fixed per review).
 *
 * Renders a SINGLE persistent container holding every participant's VideoTile,
 * and switches between the equal grid and the spotlight arrangement purely via
 * a CSS class on that container. Because the container element and the pool of
 * tiles stay mounted across the toggle, React reconciles (does not remount) the
 * tiles that remain on screen — so their <video> elements keep their attached
 * MediaStream and never re-attach/black-flash (SC-005 / C11).
 *
 * The ONLY tile that remounts on a toggle or re-pin is the single participant
 * moving into/out of the featured slot (it changes parent: pool ⇄ featured).
 * That is the explicitly-accepted cost in research.md Decision 2.
 *
 * data-testids (UI contract):
 *   layout-grid / layout-spotlight — the container (mode-dependent)
 *   spotlight-featured             — the large featured wrapper (spotlight only)
 *   spotlight-secondary            — the pool when it acts as the side column
 *
 * Click-to-pin (T014 / FR-005 / C5): in grid every tile's onSelect calls
 * pinParticipant(id) → switches to spotlight pinning it. In spotlight, the
 * side-column tiles call setPinnedId(id) to re-feature (C6).
 */
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import { useRoomStore } from '../room/store.js'
import { orderedParticipants, effectiveFeaturedId } from '../room/participants.js'
import type { PeerInfo, PeerMediaState } from '../room/store.js'
import { VideoTile } from './VideoTile.js'
import styles from './VideoGrid.module.css'

/** Stable empty stream used as a fallback when the local stream is null,
 *  so VideoTile receives a consistent object reference across renders. */
const EMPTY_STREAM = new MediaStream()

interface VideoGridProps {
  localStream: MediaStream | null
  localPeerId: string | null
  localLabel: string
  localMediaState: PeerMediaState
  peers: PeerInfo[]
}

export function VideoGrid({
  localStream,
  localPeerId,
  localLabel,
  localMediaState,
  peers: peersArray,
}: VideoGridProps) {
  const { t } = useTranslation()

  const { layoutMode, pinnedId, pinParticipant, setPinnedId, cameraFacing, peerQuality, localQuality } =
    useRoomStore(
      useShallow((s) => ({
        layoutMode: s.layoutMode,
        pinnedId: s.pinnedId,
        pinParticipant: s.pinParticipant,
        setPinnedId: s.setPinnedId,
        cameraFacing: s.cameraFacing,
        peerQuality: s.peerQuality,
        localQuality: s.localQuality,
      })),
    )

  // Build the peers record from the array (already derived in Room.tsx via useMemo).
  const peersRecord = Object.fromEntries(peersArray.map((p) => [p.peerId, p]))

  // Canonical ordered list — local first, then peers in insertion order.
  const participants = orderedParticipants(
    localPeerId,
    localStream ?? EMPTY_STREAM,
    localLabel,
    localMediaState,
    peersRecord,
    cameraFacing,
  )

  const isSpotlight = layoutMode === 'spotlight'
  const featuredId = isSpotlight ? effectiveFeaturedId(participants, pinnedId, localPeerId) : null

  // Pool = everyone in grid; everyone except the featured tile in spotlight.
  const pool = participants.filter((p) => !(isSpotlight && p.id === featuredId))
  const featured = isSpotlight ? (participants.find((p) => p.id === featuredId) ?? null) : null

  const tileLabel = (p: (typeof participants)[number]) =>
    p.isLocal ? `${t('room.you')} · ${p.label}` : p.label

  // Connection quality per tile: local uses localQuality; remotes use their
  // stored level, defaulting to 'unknown' until the first measurement (FR-001/FR-009).
  const tileQuality = (p: (typeof participants)[number]) =>
    p.isLocal ? localQuality : (peerQuality[p.id] ?? 'unknown')

  return (
    <div
      data-testid={isSpotlight ? 'layout-spotlight' : 'layout-grid'}
      className={`${styles.stage} ${isSpotlight ? styles.spotlight : styles.grid}`}
    >
      {/* Featured (large) tile — only mounted in spotlight. The single tile that
          may remount on a toggle/re-pin, by design. */}
      {isSpotlight && featured && (
        <div data-testid="spotlight-featured" className={styles.featured}>
          <VideoTile
            key={featured.id}
            stream={featured.stream}
            label={tileLabel(featured)}
            mirrored={featured.mirrored}
            muted={featured.muted}
            mediaState={featured.mediaState}
            quality={tileQuality(featured)}
          />
        </div>
      )}

      {/* Persistent pool of tiles. In grid it IS the grid; in spotlight it is the
          scrollable side column. Tiles here are keyed by id and never change
          parent across a toggle, so their streams are not re-attached. Hidden in
          spotlight when empty (e.g. only the local participant remains) so the
          featured tile fills the whole stage. */}
      {(!isSpotlight || pool.length > 0) && (
        <div data-testid={isSpotlight ? 'spotlight-secondary' : undefined} className={styles.pool}>
          {pool.map((p) => (
            <VideoTile
              key={p.id}
              stream={p.stream}
              label={tileLabel(p)}
              mirrored={p.mirrored}
              muted={p.muted}
              mediaState={p.mediaState}
              quality={tileQuality(p)}
              onSelect={isSpotlight ? () => setPinnedId(p.id) : () => pinParticipant(p.id)}
              selectLabel={t('layout.pinParticipant', { name: p.label })}
            />
          ))}
        </div>
      )}
    </div>
  )
}
